import {
  createContext,
  useContext,
  useState,
  useTransition,
  type PropsWithChildren,
} from "react";

import { useSearchParams } from "react-router";

import {
  COST_DASHBOARD_ACTION_BAR_TABS,
  getInitialActionBarState,
  updateActionBarState,
  type CostDashboardActionBarState,
} from "../utils/costDashboardActionBar";
import {
  COST_DASHBOARD_FILTER_BAR_KEY_MAP,
  type CostDashboardFilterBarState,
  getInitialFilterBarState,
  updateFilterBarState,
} from "../utils/costDashboardFilterBar";

type CostDashboardBarStateContext = {
  filterBarState: CostDashboardFilterBarState;
  applyFilterBarState: (newState: CostDashboardFilterBarState) => void;
  isFilterBarPending: boolean;
  actionBarState: CostDashboardActionBarState;
  applyActionBarState: (newState: CostDashboardActionBarState) => void;
  isActionBarPending: boolean;
  /**
   * Whether the ActionBar "Sort by" control should be shown.
   * We hide it for the Resources list, which has its own table sorting.
   */
  showActionBarSort: boolean;
} | null;

export const CostDashboardBarStateContext =
  createContext<CostDashboardBarStateContext>(null);

export const useCostDashboardBarState = () => {
  const context = useContext(CostDashboardBarStateContext);
  if (!context) {
    throw new Error(
      "useCostDashboardBarState must be used within a CostDashboardBarStateProvider",
    );
  }
  return context;
};

interface Props extends PropsWithChildren {
  /**
   * Whether the bars belong to an opportunity's details page rather than the
   * dashboard's list of opportunities.
   */
  hasSelectedOpportunity?: boolean;
}

export function CostDashboardBarStateProvider({
  hasSelectedOpportunity = false,
  children,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filterBarState, setFilterBarState] = useState(() =>
    getInitialFilterBarState(searchParams),
  );

  const [actionBarState, setActionBarState] = useState(() =>
    getInitialActionBarState(searchParams, hasSelectedOpportunity),
  );

  const [isFilterBarPending, startFilterBarTransition] = useTransition();
  const [isActionBarPending, startActionBarTransition] = useTransition();

  function applyFilterBarState(newState: CostDashboardFilterBarState) {
    setFilterBarState((prevState) => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        ...newState.filters,
      },
      locked:
        newState.locked !== undefined ? newState.locked : prevState.locked,
    }));

    startFilterBarTransition(() =>
      updateFilterBarState(
        { ...filterBarState, ...newState },
        searchParams,
        setSearchParams,
      ),
    );
  }

  function applyActionBarState(newState: CostDashboardActionBarState) {
    setActionBarState((prevState) => ({
      ...prevState,
      ...newState,
    }));

    startActionBarTransition(() =>
      updateActionBarState(
        { ...actionBarState, ...newState },
        searchParams,
        setSearchParams,
      ),
    );
  }

  // set a default tab on the opportunity details view when navigating from the list view
  // as "opportunities" is not a valid tab in that context
  if (
    hasSelectedOpportunity &&
    actionBarState.tab === COST_DASHBOARD_ACTION_BAR_TABS.default.opportunities
  ) {
    setActionBarState((prevState) => ({
      ...prevState,
      tab: COST_DASHBOARD_ACTION_BAR_TABS.opportunitySelected.accounts,
    }));
  }

  // the search and the action bar state are scoped to the view they were set
  // on: an opportunity-title search from the list doesn't apply to the
  // accounts/tags/resources of a selected opportunity, and each view has its
  // own tab set. Whenever we cross between the list and details views, re-sync
  // both from the URL, as on first load: in-app links drop the query params,
  // landing on the view's defaults instead of carrying over state that would
  // filter the lists invisibly, while a destination URL carrying its own
  // params (deep link, browser history) restores exactly the state it shows.
  const [prevHasSelectedOpportunity, setPrevHasSelectedOpportunity] = useState(
    hasSelectedOpportunity,
  );
  if (prevHasSelectedOpportunity !== hasSelectedOpportunity) {
    setPrevHasSelectedOpportunity(hasSelectedOpportunity);
    setActionBarState(
      getInitialActionBarState(searchParams, hasSelectedOpportunity),
    );
    const urlSearch = searchParams.get(
      COST_DASHBOARD_FILTER_BAR_KEY_MAP.filters.search,
    );
    const search = urlSearch ? urlSearch.split(",") : [];
    if (search.join(",") !== filterBarState.filters.search.join(",")) {
      setFilterBarState((prevState) => ({
        ...prevState,
        filters: { ...prevState.filters, search },
      }));
    }
  }

  const showActionBarSort =
    actionBarState.tab !== COST_DASHBOARD_ACTION_BAR_TABS.default.resources;

  return (
    <CostDashboardBarStateContext.Provider
      value={{
        filterBarState,
        applyFilterBarState,
        isFilterBarPending,
        actionBarState,
        applyActionBarState,
        isActionBarPending,
        showActionBarSort,
      }}
    >
      {children}
    </CostDashboardBarStateContext.Provider>
  );
}
